import '@scss/store/main.scss'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.min.css'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-fade'
import 'swiper/css/thumbs'

import * as ActiveStorage from "@rails/activestorage";
import { Turbo } from "@hotwired/turbo-rails";
import TurboPower from "turbo_power";
import { createIcons, icons } from "lucide";
import "@app/channels/channel_manager";
import { Application } from "@hotwired/stimulus";
import { StimulusLoader } from "@app/stimulus_loader";
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade, Thumbs } from 'swiper/modules';

import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

import "@videojs/html/video/player";
import "@videojs/html/video/minimal-skin";
import "@videojs/html/video/minimal-skin.css";

TurboPower.initialize(Turbo.StreamActions)
Turbo.StreamActions.redirect = function () {
  const url = this.getAttribute("target")
  // Adds a new entry to the browser history
  history.pushState({}, "", url);
  // Modifies the current history entry instead of adding a new one
  history.replaceState({}, "", url);
}

window.Fancybox = Fancybox;

window.activeStorage = ActiveStorage.start();
window.application = Application.start();

window.storePage = new StimulusLoader({
  controllers: {
    ...import.meta.glob("@app/controllers/store_page/*_controller.js", { eager: true }),
    ...import.meta.glob("@app/controllers/components/select2_controller.js", { eager: true }),
    ...import.meta.glob("@app/controllers/components/modal_controller.js", { eager: true }),
    ...import.meta.glob("@app/controllers/components/venue_map_controller.js", { eager: true }),
    ...import.meta.glob("@app/controllers/components/phone_input_controller.js", { eager: true }),
    ...import.meta.glob("@app/controllers/components/dropdown_controller.js", { eager: true }),
    ...import.meta.glob("@app/controllers/operator_page/notification_dropdown_controller.js", { eager: true }),
  },
});
window.storePage.registerControllers();

function setupFancybox() {
  console.log("setupFancybox");

  Fancybox.unbind("[data-fancybox]");
  Fancybox.close(true);
  Fancybox.bind("[data-fancybox]", {
    Hash: false,
    placeFocusBack: false,
    autoFocus: false,
    transition: "fade",
    animated: true,
    dragToClose: false,

    keyboard: {
      Escape: "close",
      ArrowRight: "next",
      ArrowLeft: "prev",
    },

    width: "90%",
    height: "auto",

    Html: {
      video: {
        autoplay: true,
        controls: true,
      },
    },

    on: {
      ready: (fancybox) => {
        const slide = fancybox.getSlide();
        const video = slide?.el?.querySelector("video");
        if (video) {
          video.muted = true;
          video.play();
          if (!video.closest("video-player")) {
            video.addEventListener("click", () => {
              video.paused ? video.play() : video.pause();
            });
          }
        }
      },
      "Carousel.change": (fancybox) => {
        document.querySelectorAll('div[id^="video-"] video, .fancybox__container video').forEach((video) => {
          video.pause();
          video.currentTime = 0;
        });
      },
      close: (fancybox) => {
        document.querySelectorAll('div[id^="video-"] video, .fancybox__container video').forEach((video) => {
          video.pause();
          video.currentTime = 0;
        });
      },
      "Carousel.ready": (fancybox) => {
        const slide = fancybox.getSlide();
        const video = slide?.el?.querySelector("video");
        if (video) {
          video.muted = true;
          video.play();

          if (!video.closest("video-player")) {
            video.addEventListener("click", () => {
              if (video.paused) {
                video.play();
              } else {
                video.pause();
              }
            });
          }
        }
      },
    },
  });
}
document.addEventListener("turbo:load", setupFancybox);
document.addEventListener("turbo:load", () => createIcons({ icons }));

// ── Swiper ───────────────────────────────────────────────────────────────────

const _swipers = []

function initSwipers() {
  destroySwipers()

  // Hero Swiper — home page
  const heroEl = document.querySelector('.sky-hero-swiper')
  if (heroEl) {
    _swipers.push(new Swiper(heroEl, {
      modules: [Autoplay, EffectFade, Pagination],
      effect: 'fade',
      fadeEffect: { crossFade: true },
      autoplay: { delay: 5500, disableOnInteraction: false },
      loop: true,
      speed: 900,
      pagination: { el: '.sky-hero-pagination', clickable: true }
    }))
  }

  // Popular Venues strip — home page
  const venuesEl = document.querySelector('.sky-venues-swiper')
  if (venuesEl) {
    _swipers.push(new Swiper(venuesEl, {
      modules: [Navigation],
      slidesPerView: 'auto',
      spaceBetween: 16,
      navigation: { nextEl: '.sky-venues-next', prevEl: '.sky-venues-prev' }
    }))
  }

  // Testimonials — home page
  const testimonialsEl = document.querySelector('.sky-testimonials-swiper')
  if (testimonialsEl) {
    _swipers.push(new Swiper(testimonialsEl, {
      modules: [Autoplay, Pagination],
      autoplay: { delay: 4500, disableOnInteraction: false, pauseOnMouseEnter: true },
      loop: true,
      slidesPerView: 1,
      spaceBetween: 16,
      breakpoints: {
        640: { slidesPerView: 2, spaceBetween: 20 },
        1024: { slidesPerView: 3, spaceBetween: 24 }
      },
      pagination: { el: '.sky-testimonials-pagination', clickable: true }
    }))
  }

  // Best Packages carousel — home page
  const packagesEl = document.querySelector('.sky-packages-swiper')
  if (packagesEl) {
    _swipers.push(new Swiper(packagesEl, {
      modules: [Navigation],
      slidesPerView: 'auto',
      spaceBetween: 20,
      navigation: { nextEl: '.sky-packages-next', prevEl: '.sky-packages-prev' }
    }))
  }

  // Drone Points carousel — home page
  const dronePointsEl = document.querySelector('.sky-drone-points-swiper')
  if (dronePointsEl) {
    _swipers.push(new Swiper(dronePointsEl, {
      modules: [Navigation],
      slidesPerView: 'auto',
      spaceBetween: 16,
      navigation: { nextEl: '.sky-drone-points-next', prevEl: '.sky-drone-points-prev' }
    }))
  }

  // Packages at drone point — drone_points/show
  const dpPkgsEl = document.querySelector('.sky-dp-pkgs-swiper')
  if (dpPkgsEl) {
    _swipers.push(new Swiper(dpPkgsEl, {
      modules: [Navigation],
      slidesPerView: 'auto',
      spaceBetween: 16,
      navigation: { nextEl: '.sky-dp-pkgs-next', prevEl: '.sky-dp-pkgs-prev' }
    }))
  }

  // Featured Venues — venues index page
  const featuredEl = document.querySelector('.sky-featured-swiper')
  if (featuredEl) {
    _swipers.push(new Swiper(featuredEl, {
      modules: [Navigation, Autoplay],
      slidesPerView: 'auto',
      spaceBetween: 20,
      autoplay: { delay: 4200, disableOnInteraction: false, pauseOnMouseEnter: true },
      navigation: { nextEl: '.sky-featured-next', prevEl: '.sky-featured-prev' }
    }))
  }

  // Drone Point gallery — main + synced thumbs
  const dpThumbsEl = document.querySelector('.sky-dp-thumbs-swiper')
  const dpMainEl = document.querySelector('.sky-dp-main-swiper')
  if (dpMainEl && dpThumbsEl) {
    const thumbsSwiper = new Swiper(dpThumbsEl, {
      modules: [],
      slidesPerView: 'auto',
      spaceBetween: 8,
      watchSlidesProgress: true
    })
    _swipers.push(thumbsSwiper)
    _swipers.push(new Swiper(dpMainEl, {
      modules: [Navigation, Thumbs],
      thumbs: { swiper: thumbsSwiper },
      loop: false,
      navigation: { nextEl: '.sky-dp-next', prevEl: '.sky-dp-prev' }
    }))
  }
}

function destroySwipers() {
  _swipers.forEach(s => { try { s.destroy(true, true) } catch (_) { } })
  _swipers.length = 0
}

document.addEventListener('turbo:load', initSwipers)
document.addEventListener('turbo:before-cache', destroySwipers)

// Handle Spacebar to play/pause video inside Fancybox
document.addEventListener("keydown", function (e) {
  if (e.code === "Space" && e.target.tagName !== "INPUT" && e.target.tagName !== "TEXTAREA") {
    // Find the currently active video in Fancybox (if any)
    const activeVideo = document.querySelector(".fancybox__slide.is-selected video, .fancybox__slide.has-video video, .f-carousel__slide.is-selected video");

    if (activeVideo) {
      e.preventDefault(); // Prevent page scroll
      if (activeVideo.paused) {
        activeVideo.play();
      } else {
        activeVideo.pause();
      }
    }
  }
});
